import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, getDoc, doc, Timestamp } from 'firebase/firestore';
import { db as firebaseDb } from '../firebase';
import { Clock, Calendar } from 'lucide-react';

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const getWeekDates = (referenceDate) => {
  const start = new Date(referenceDate);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay(); // 0=Sun,1=Mon,...
  const diff = day === 0 ? -6 : 1 - day; // Monday first
  start.setDate(start.getDate() + diff);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const EmployeeWeekView = ({ db = firebaseDb, employee, currentDate, hasPermission }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekDates = getWeekDates(currentDate);

  const fetchAppointments = useCallback(async () => {
    if (!db || !employee?.id) return;
    setLoading(true);
    try {
      const startOfWeek = new Date(weekDates[0]);
      const endOfWeek = new Date(weekDates[6]);
      endOfWeek.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, 'appointments'),
        where('employeeId', '==', employee.id),
        where('date', '>=', Timestamp.fromDate(startOfWeek)),
        where('date', '<=', Timestamp.fromDate(endOfWeek))
      );

      const snap = await getDocs(q);
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Enrichir avec les données client (nom + couleur)
      const clientCache = new Map();
      const enriched = await Promise.all(raw.map(async apt => {
        if (!apt.clientId) return apt;
        if (!clientCache.has(apt.clientId)) {
          const clientSnap = await getDoc(doc(db, 'clients', apt.clientId));
          clientCache.set(apt.clientId, clientSnap.exists() ? clientSnap.data() : null);
        }
        const clientData = clientCache.get(apt.clientId);
        return {
          ...apt,
          clientName: clientData ? `${clientData.lastName || ''} ${clientData.firstName || ''}`.trim() : 'Bénéficiaire inconnu',
          clientColor: clientData?.color || '#3b82f6',
        };
      }));
      setAppointments(enriched);
    } catch (e) {
      console.error('EmployeeWeekView: fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [db, employee?.id, currentDate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const getAptsForDay = (date) =>
    appointments
      .filter(apt => {
        const d = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
        return isSameDay(d, date);
      })
      .sort((a, b) => {
        const [ah, am] = (a.startTime || '0:0').split(':').map(Number);
        const [bh, bm] = (b.startTime || '0:0').split(':').map(Number);
        return (ah * 60 + am) - (bh * 60 + bm);
      });

  // Weekly totals
  const totalMin = appointments.reduce((acc, apt) => {
    const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
    const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
    return acc + ((eh * 60 + em) - (sh * 60 + sm));
  }, 0);
  const totalLabel = `${Math.floor(totalMin / 60)}h${totalMin % 60 > 0 ? String(totalMin % 60).padStart(2, '0') : ''}`;

  const today = new Date();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Summary bar */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 text-sm flex-shrink-0">
        <span className="text-gray-600 dark:text-gray-400">
          <strong className="text-gray-900 dark:text-gray-100">{appointments.length}</strong> intervention{appointments.length > 1 ? 's' : ''} cette semaine
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span className="text-gray-600 dark:text-gray-400">
          <strong className="text-gray-900 dark:text-gray-100">{totalLabel}</strong> au total
        </span>
      </div>

      {/* Week grid */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="grid grid-cols-7 h-full min-w-[560px] divide-x divide-gray-200 dark:divide-gray-700">
          {weekDates.map((date, idx) => {
            const isToday = isSameDay(date, today);
            const dayApts = getAptsForDay(date);

            // Day total hours
            const dayMin = dayApts.reduce((acc, apt) => {
              const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
              const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
              return acc + ((eh * 60 + em) - (sh * 60 + sm));
            }, 0);

            return (
              <div
                key={idx}
                className={`flex flex-col min-h-0 ${isToday ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
              >
                {/* Day header */}
                <div className={`p-2 text-center border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-900'}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
                    {DAY_LABELS[idx]}
                  </p>
                  <p className={`text-xl font-bold leading-tight ${isToday ? 'text-blue-600 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                    {date.getDate()}
                  </p>
                  {dayApts.length > 0 ? (
                    <div className="mt-0.5">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${isToday ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                        {dayApts.length}
                      </span>
                      {dayMin > 0 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {Math.floor(dayMin / 60)}h{dayMin % 60 > 0 ? String(dayMin % 60).padStart(2, '0') : ''}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-300 dark:text-gray-600 mt-0.5">—</p>
                  )}
                </div>

                {/* Appointments */}
                <div className="flex-1 overflow-y-auto p-1 space-y-1">
                  {dayApts.map(apt => {
                    const now = new Date();
                    const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
                    const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
                    const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
                    const start = new Date(aptDate); start.setHours(sh, sm, 0, 0);
                    const end = new Date(aptDate); end.setHours(eh, em, 0, 0);
                    const isCurrent = start <= now && end >= now;
                    const clientColor = apt.clientColor || '#3b82f6';

                    return (
                      <div
                        key={apt.id}
                        className={`rounded-lg p-2 text-xs transition-all hover:shadow-sm ${isCurrent ? 'ring-2 ring-amber-400/60' : ''}`}
                        style={{
                          backgroundColor: `${clientColor}18`,
                          borderLeft: `3px solid ${clientColor}`
                        }}
                      >
                        <p className="font-semibold text-gray-800 dark:text-gray-100 truncate leading-tight">
                          {apt.clientName || '—'}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 flex items-center gap-0.5 mt-0.5">
                          <Clock size={9} className="flex-shrink-0" />
                          <span className="truncate">{apt.startTime}–{apt.endTime}</span>
                        </p>
                        {isCurrent && (
                          <span className="inline-block mt-1 px-1.5 py-0.5 bg-amber-500 text-white rounded-full text-[10px] font-medium">
                            En cours
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmployeeWeekView;
